import { useEffect, useRef, useState } from 'react';

// Same framing as /logo-animated.svg (viewBox 1100×830), so the two swap without a jump
const MARK_RATIO = 'aspect-[1100/830]';
const INTRO_MS = 4200;
// First-visit intro (#intro, created in public/index.html)
const INTRO_MIN_MS = 1100;   // let the car drive at least this long
const INTRO_MAX_WAIT = 3000; // never hold the page longer than this waiting for "load"
const FLY_MS = 850;          // matches the #intro img transition in index.html
const PARK_MS = 1300;        // header car keeps rolling briefly after it lands

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The rabbit-in-a-wooden-car mark. Shows the still image (light, instant);
 * when `playing`, the animated SVG is layered on top and the still is hidden
 * only once the animation has loaded, so there is never a blank frame.
 */
export const LogoMark = ({ playing = false, hidden = false, markRef, className = 'h-11' }) => {
  const [animReady, setAnimReady] = useState(false);
  const showAnim = playing && animReady;
  return (
    <span ref={markRef} className={`relative block ${MARK_RATIO} flex-shrink-0 ${hidden ? 'invisible' : ''} ${className}`}>
      <img src="/logo-mark.webp" alt="Wooden rabbit driving a toy car" width="330" height="249"
        className={`absolute inset-0 w-full h-full transition-opacity duration-200 ${showAnim ? 'opacity-0' : 'opacity-100'}`} />
      {playing && (
        <img src="/logo-animated.svg" alt="" aria-hidden="true" width="330" height="249"
          onLoad={() => setAnimReady(true)}
          className={`absolute inset-0 w-full h-full ${showAnim ? 'opacity-100' : 'opacity-0'}`} />
      )}
    </span>
  );
};

/**
 * Fly the intro car from the centre of the screen into the header logo's spot.
 * Resolves once it has landed and the overlay is gone.
 */
const flyIntoHeader = (intro, target) => new Promise(resolve => {
  const img = intro.querySelector('img');
  const from = img.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  intro.dataset.flying = '1';
  img.style.transform =
    `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${to.width / from.width})`;
  intro.classList.add('flying');
  setTimeout(() => {
    intro.classList.add('done');
    resolve();
    setTimeout(() => intro.remove(), 400);
  }, FLY_MS);
});

/**
 * Header / footer lockup: mark + the logo's own "THRAYAM TOYS" lettering.
 * Header: on a visit's first page the intro car flies in and parks here;
 * otherwise the logo plays once after load. Hover replays it.
 */
const BrandLogo = ({ variant = 'header' }) => {
  const [playing, setPlaying] = useState(false);
  const [introActive, setIntroActive] = useState(
    () => variant === 'header' && typeof document !== 'undefined' && !!document.getElementById('intro')
  );
  const timer = useRef();
  const markRef = useRef();

  const play = (ms) => {
    if (prefersReducedMotion()) return;
    clearTimeout(timer.current);
    setPlaying(true);
    if (ms) timer.current = setTimeout(() => setPlaying(false), ms);
  };

  useEffect(() => {
    if (variant !== 'header') return undefined;
    let cancelled = false;
    const intro = document.getElementById('intro');

    if (intro && !intro.dataset.flying) {
      // Hand-off: wait for the page to load (and a minimum drive), then fly into the header
      const shownAt = performance.now();
      const land = () => {
        if (cancelled || !markRef.current) return;
        const wait = Math.max(0, INTRO_MIN_MS - (performance.now() - shownAt));
        setTimeout(() => {
          if (cancelled || !markRef.current) return;
          play(PARK_MS + FLY_MS);
          flyIntoHeader(intro, markRef.current).then(() => !cancelled && setIntroActive(false));
        }, wait);
      };
      const cap = setTimeout(land, INTRO_MAX_WAIT);
      const onLoad = () => { clearTimeout(cap); land(); };
      if (document.readyState === 'complete') onLoad();
      else window.addEventListener('load', onLoad, { once: true });
      return () => {
        cancelled = true;
        clearTimeout(cap);
        window.removeEventListener('load', onLoad);
        clearTimeout(timer.current);
      };
    }

    // No intro (later pages of the visit): play once after the page has loaded
    const start = () => play(INTRO_MS);
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener('load', start);
      clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const hover = {
    onMouseEnter: () => play(),
    onMouseLeave: () => play(1200), // let it finish a bump before settling
    onFocus: () => play(),
    onBlur: () => play(1200),
  };

  if (variant === 'footer') {
    return (
      <span className="inline-flex items-center gap-3" {...hover}>
        <LogoMark playing={playing} className="h-16" />
        <img src="/logo-wordmark-stacked-light.png" alt="Thrayam Toys" width="480" height="174" loading="lazy"
          className="h-14 w-auto" />
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 lg:gap-2" {...hover}>
      {/* Hidden (space kept) while the intro car is on its way, so there's only one car on screen */}
      <LogoMark playing={playing} hidden={introActive} markRef={markRef} className="h-11 lg:h-14" />
      <img src="/logo-wordmark.png" alt="Thrayam Toys" width="624" height="60"
        className="h-[15px] sm:h-[17px] lg:h-5 w-auto" />
    </span>
  );
};

export default BrandLogo;
